import {
  MenuItem,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  readChildAchievements,
  readChildDashboard,
  resetChildTrainingToday,
  searchChildren,
} from '../api/endpoints';
import type { AdminChild, ChildAchievements, ChildDashboard, Paginated } from '../api/types';
import { AchievementPanel } from '../components/AchievementPanel';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { TableFilters } from '../components/TableFilters';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { useSort } from '../components/useSort';
import { useAdminMutation } from '../api/useAdminMutation';

const TREND_MARK: Record<string, string> = { up: '↑', down: '↓', flat: '→', unknown: '·' };
const TREND_COLOUR: Record<string, string> = {
  up: 'success.main',
  down: 'warning.main',
  flat: 'text.secondary',
  unknown: 'text.disabled',
};

/** The columns the API will order by. It matches these against a fixed list; anything else is ignored. */
type SortKey = 'nickname' | 'birthYear' | 'schoolGrade' | 'createdAt' | 'status';

const GRADES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const ChildrenPage = () => {
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const [grade, setGrade] = useState('');
  const [status, setStatus] = useState('');
  const sort = useSort<SortKey>('createdAt', 'desc', () => setPage(0));
  const [selected, setSelected] = useState<AdminChild | null>(null);
  const [resetting, setResetting] = useState(false);
  // Invalidates the dashboard too: the counts in this very dialog are read from it.
  const reset = useAdminMutation(resetChildTrainingToday, ['children', 'child-dashboard']);

  const list = useQuery<Paginated<AdminChild>>({
    queryKey: ['children', term, page, sort.key, sort.direction, grade, status],
    queryFn: () =>
      searchChildren(
        term,
        page + 1,
        sort.key,
        sort.direction,
        grade === '' ? undefined : Number(grade),
        status || undefined,
      ),
  });

  const detail = useQuery<ChildDashboard>({
    queryKey: ['child-dashboard', selected?.id],
    queryFn: () => readChildDashboard(selected!.id),
    enabled: selected !== null,
  });

  const achievements = useQuery<ChildAchievements>({
    queryKey: ['child-achievements', selected?.id],
    queryFn: () => readChildAchievements(selected!.id),
    enabled: selected !== null,
  });

  const items = list.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Deti"
        subtitle="Profily detí sú vedené len pod prezývkou a rokom narodenia — meno ani dátum narodenia sa nezbierajú. Kliknutím na riadok otvoríte ten istý prehľad, aký vidí rodič."
      />

      <ErrorNotice error={list.error} onRetry={() => void list.refetch()} />

      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label="Hľadať prezývku"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 240 }}
          />
          <TextField
            size="small"
            select
            label="Ročník"
            value={grade}
            onChange={(event) => {
              setGrade(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {GRADES.map((one) => (
              <MenuItem key={one} value={String(one)}>
                {one}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Stav"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">všetky</MenuItem>
            <MenuItem value="active">aktívne</MenuItem>
            <MenuItem value="archived">archivované</MenuItem>
          </TextField>
        </TableFilters>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="nickname">
                  Prezývka
                </SortableCell>
                <SortableCell sort={sort} column="birthYear">
                  Rok narodenia
                </SortableCell>
                <SortableCell sort={sort} column="schoolGrade">
                  Ročník
                </SortableCell>
                <SortableCell sort={sort} column="createdAt">
                  Vytvorený
                </SortableCell>
                <SortableCell sort={sort} column="status">
                  Stav
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.isLoading ? <TableSkeletonRows columns={5} /> : null}
              {!list.isLoading &&
                items.map((child: AdminChild) => (
                  <TableRow
                    key={child.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelected(child)}
                  >
                    <TableCell sx={{ fontWeight: 560 }}>{child.nickname}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{child.birthYear}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{child.schoolGrade}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {new Date(child.createdAt).toLocaleDateString('sk-SK')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={child.archivedAt ? 'archivovaný' : 'aktívny'}
                        color={child.archivedAt ? 'default' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!list.isLoading && items.length === 0 ? (
          <EmptyState
            title={term ? 'Žiadne dieťa nezodpovedá hľadaniu' : 'Zatiaľ tu nie sú žiadne profily detí'}
            detail={term ? `Skúste inú prezývku než „${term}".` : 'Profil dieťaťa vytvára rodič v aplikácii.'}
          />
        ) : null}

        <TablePagination
          component="div"
          count={list.data?.total ?? 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={list.data?.pageSize ?? 25}
          rowsPerPageOptions={[list.data?.pageSize ?? 25]}
          labelRowsPerPage="Na stranu"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </Paper>

      <Dialog open={selected !== null} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {selected?.nickname}
          {detail.data ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {detail.data.trainingDayCount} tréningových dní · {detail.data.totalAttemptCount} odpovedí
              {detail.data.inCalibration ? ' · prebieha úvodný týždeň' : ''}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          {detail.isLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
          <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />

          {detail.data ? (
            <>
              <Stack spacing={2}>
                {detail.data.skills.map((skill) => (
                  <Box key={skill.skillCode}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      sx={{ mb: 0.75 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 560 }}>
                        {skill.skillName}{' '}
                        <Box component="span" sx={{ color: TREND_COLOUR[skill.trend] ?? 'text.disabled' }}>
                          {TREND_MARK[skill.trend] ?? '·'}
                        </Box>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {skill.level}/{skill.maximumLevel} · {Math.round(skill.accuracy * 100)} %
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(skill.level / skill.maximumLevel) * 100}
                      sx={{ height: 6 }}
                    />
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <Stack spacing={1}>
                {detail.data.recommendations.map((recommendation, index) => (
                  <Typography key={index} variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {recommendation.text}
                  </Typography>
                ))}
              </Stack>
            </>
          ) : null}

          {achievements.data ? (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Ocenenia
              </Typography>
              <AchievementPanel data={achievements.data} />
            </>
          ) : null}
          <ErrorNotice error={achievements.error} onRetry={() => void achievements.refetch()} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {/*
            A staff tool, not a feature: a child gets one session a day, so testing a challenge otherwise
            means waiting a day or creating another child (R0129). It sits behind a confirmation that names
            the child, because it discards a record of what that child did today.
          */}
          <Box>
            <Button
              color="warning"
              onClick={() => setResetting(true)}
              disabled={reset.isPending || selected === null}
            >
              Zahodiť dnešný tréning
            </Button>
            {reset.data ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {reset.data.discarded
                  ? 'Zahodené. Ďalší tréning sa poskladá nanovo.'
                  : 'Dieťa dnes netrénovalo, nebolo čo zahodiť.'}
              </Typography>
            ) : null}
          </Box>
          <Button onClick={() => setSelected(null)}>Zavrieť</Button>
        </DialogActions>
        <ErrorNotice error={reset.error} />
      </Dialog>

      <Dialog open={resetting} onClose={() => setResetting(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Zahodiť dnešný tréning?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Dnešná session dieťaťa <strong>{selected?.nickname}</strong> sa označí ako zahodená a ďalší
            tréning sa poskladá nanovo. Odpovede, ktoré dieťa dnes dalo, sa nemažú — zostávajú v histórii aj v
            štatistikách.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetting(false)}>Zrušiť</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={reset.isPending}
            onClick={() => {
              if (selected !== null) reset.mutate(selected.id);
              setResetting(false);
            }}
          >
            Zahodiť
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
